import { Suspense, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Building2, LayoutDashboard, LogOut, Menu, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { PageSkeleton } from '@/components/layout/PageSkeleton';

const NAV = [
  { to: '/superadmin', label: 'Visão da plataforma', icon: LayoutDashboard, end: true },
  { to: '/superadmin/organizacoes', label: 'Organizações', icon: Building2, end: false },
  { to: '/superadmin/admins', label: 'Admins', icon: ShieldCheck, end: false },
];

function SuperadminNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="flex flex-col gap-0.5">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150',
              isActive
                ? 'bg-gradient-to-r from-azure to-primary font-semibold text-white shadow-[0_4px_14px_hsl(var(--azure)/0.45)]'
                : 'text-[hsl(var(--rail-fg-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--rail-fg))]',
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          {label}
        </NavLink>
      ))}
    </div>
  );
}

function RailBrand() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <img src="/logo-simbolo-branco.png" alt="" aria-hidden className="h-7 w-7 object-contain" />
      </span>
      <span className="min-w-0 leading-none">
        <span className="font-display text-[15px] font-bold text-white">
          Agiliza<span className="text-brand">OS</span>
        </span>
        <span className="mt-0.5 block text-[9px] tracking-wide text-[hsl(var(--rail-fg-muted))]/80">
          Painel da plataforma
        </span>
      </span>
    </div>
  );
}

export function SuperadminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleSair = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <a
        href="#conteudo-principal"
        className="fixed left-4 top-2 z-[100] -translate-y-[200%] rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-md transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Ir para o conteúdo
      </a>

      <aside
        className={cn(
          'hidden w-56 flex-col text-[hsl(var(--rail-fg))] md:flex',
          'bg-gradient-to-b from-[hsl(var(--rail-from))] to-[hsl(var(--rail-to))]',
          'shadow-[inset_-1px_0_0_0_hsl(213_45%_60%/0.16)]',
        )}
      >
        <div className="flex h-14 items-center border-b border-white/10 px-4">
          <RailBrand />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Plataforma">
          <p className="mb-1 flex items-center gap-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--rail-fg-muted))]/70">
            <span className="h-[3px] w-2 rounded-full bg-azure" aria-hidden />
            Gestão
          </p>
          <SuperadminNav />
        </nav>

        <div className="space-y-1.5 px-5 pb-3 pt-2" aria-hidden>
          <span className="block h-[3px] w-2/5 rounded-full bg-azure/35" />
          <span className="block h-[3px] w-3/5 rounded-full bg-azure/20" />
          <span className="block h-[3px] w-1/4 rounded-full bg-azure/10" />
        </div>

        <div className="border-t border-white/10 p-3">
          <div className="mb-2 flex items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-azure to-primary text-xs font-bold text-white"
              aria-hidden
            >
              {user?.nome?.charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-xs font-semibold text-[hsl(var(--rail-fg))]">
                {user?.nome}
              </span>
              <span className="block truncate text-[10px] text-[hsl(var(--rail-fg-muted))]/80">
                {user?.email}
              </span>
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-[hsl(var(--rail-fg-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--rail-fg))]"
            onClick={handleSair}
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            Sair
          </Button>
        </div>
      </aside>

      <Dialog open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <DialogContent
          className={cn(
            'fixed left-0 top-0 z-50 flex h-full max-h-screen w-[min(100vw-1rem,20rem)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 text-[hsl(var(--rail-fg))] shadow-xl sm:max-w-none',
            'bg-gradient-to-b from-[hsl(var(--rail-from))] to-[hsl(var(--rail-to))]',
            'data-[state=closed]:slide-out-to-left-2 data-[state=open]:slide-in-from-left-2',
          )}
        >
          <DialogHeader className="border-b border-white/10 px-4 py-3 text-left">
            <DialogTitle className="text-white">
              <RailBrand />
            </DialogTitle>
            <DialogDescription className="sr-only">Menu de navegação</DialogDescription>
          </DialogHeader>
          <nav className="flex-1 overflow-y-auto px-2 py-3">
            <p className="mb-1 flex items-center gap-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--rail-fg-muted))]/70">
              <span className="h-[3px] w-2 rounded-full bg-azure" aria-hidden />
              Gestão
            </p>
            <SuperadminNav onNavigate={() => setMobileNavOpen(false)} />
          </nav>
          <div className="border-t border-white/10 p-3">
            <div className="mb-2 px-1">
              <p className="truncate text-xs font-semibold">{user?.nome}</p>
              <p className="truncate text-[10px] text-[hsl(var(--rail-fg-muted))]">{user?.email}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-[hsl(var(--rail-fg-muted))] hover:bg-white/[0.06] hover:text-[hsl(var(--rail-fg))]"
              onClick={() => {
                setMobileNavOpen(false);
                handleSair();
              }}
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Sair
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card px-3 sm:px-6">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            aria-label="Abrir menu de navegação"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden />
          </Button>
          <span className="truncate text-xs font-semibold uppercase tracking-widest text-primary">
            Administração da plataforma
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-azure to-primary text-[11px] font-bold text-white"
              aria-hidden
            >
              {user?.nome?.charAt(0).toUpperCase()}
            </span>
            <span className="hidden text-sm text-foreground sm:inline">{user?.nome}</span>
          </div>
        </header>
        <main
          id="conteudo-principal"
          className="flex-1 overflow-auto p-4 sm:p-6"
          tabIndex={-1}
          style={{
            backgroundImage:
              'radial-gradient(90% 160px at 18% 0%, hsl(var(--azure) / 0.12), transparent 70%), radial-gradient(55% 130px at 88% 0%, hsl(var(--brand) / 0.06), transparent 70%)',
          }}
        >
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
